import { ShirtspaceResponseMessage } from "@services/ShirtspaceService/ShirtspaceService.types";
import get from "lodash/get";
import { FC, useEffect, useRef, useState } from "react";
import { GoogleReCaptcha } from "react-google-recaptcha-v3";
import { useFetcher, useMatches, useOutletContext } from "react-router";
import FlashMessage from "~/components/FlashMessage";
import { CreateAccountActionResponse } from "~/entities/actions/types";
import { RootLoaderContext, WindowWithGTM } from "~/entities/global/types";
import { GA4SignUpEvent } from "~/entities/gtm/types";
import { RewardsParticipant } from "~/entities/rewards/types";
import { RouteIds } from "~/routeIds";
import FieldError from "./FieldError";
import SubmitButton from "./SubmitButton";

const ErrorMessage: React.FC<{
  error?: ShirtspaceResponseMessage;
}> = ({ error }) => {
  if (!error) return null;
  return (
    <FlashMessage
      kind="failure"
      message={error.text}
    />
  );
};

type Props = {
  onSuccess: (
    customerType: string,
    rewardsParticipant: RewardsParticipant | null,
  ) => void;
  isActive: boolean;
};

const SignInCta: FC = () => {
  const context = useOutletContext<RootLoaderContext>();
  const matches = useMatches();
  const isProductPage = matches.some(
    (match) => match.id === RouteIds.PAGES_PRODUCT,
  );

  if (isProductPage)
    return (
      <button
        type="button"
        onClick={() => {
          context.setCreateAccountWizardOpen(false);
          context.setLoginModalOpen(true);
        }}
        className="text-blue900 underline"
      >
        Sign in to your account
      </button>
    );

  return (
    <a
      className="text-sm text-blue900 underline"
      href="/users/sign_in"
    >
      Sign in to your account
    </a>
  );
};

const CreateAccount: React.FC<Props> = ({ onSuccess, isActive }) => {
  const [errors, setErrors] = useState<
    Record<string, ShirtspaceResponseMessage>
  >({});
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [token, setToken] = useState("");
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  const fetcher = useFetcher<CreateAccountActionResponse>();

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const passwordConfirmationRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPassword("");
    setPasswordConfirmation("");
    if (
      get(fetcher, "data.ok") === true &&
      get(fetcher, "data.data.customer_type")
    ) {
      setErrors({});
      const gtmEvents = get(fetcher, "data.data.gtmEvents", []);
      gtmEvents.forEach((event: GA4SignUpEvent) => {
        (window as WindowWithGTM).dataLayer?.push(event);
      });
      const customerType = get(fetcher, "data.data.customer_type");
      const rewardsParticipant = get(
        fetcher,
        "data.data.rewardsParticipant",
        null,
      );
      if (typeof customerType === "string") {
        onSuccess(customerType, rewardsParticipant);
      }
    } else {
      const messages = get(fetcher, "data.messages", []);

      const errorsHash = messages.reduce(
        (
          hash: Record<string, ShirtspaceResponseMessage>,
          message: ShirtspaceResponseMessage,
        ) => {
          hash[message.attribute ?? "form"] = message;
          return hash;
        },
        {},
      );
      setErrors(errorsHash);
    }
  }, [fetcher.data]);

  useEffect(() => {
    if (errors["email"] && emailRef.current) {
      emailRef.current.focus();
    } else if (
      (errors["password"] || errors["password_confirmation"]) &&
      passwordRef.current
    ) {
      passwordRef.current.focus();
    } else if (isActive && emailRef.current) {
      emailRef.current.focus();
    }
  }, [isActive, errors]);

  if (!isActive) return null;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.name === "password") {
      setPassword(event.target.value);
    } else if (event.target.name === "password_confirmation") {
      setPasswordConfirmation(event.target.value);
    }
  };

  const handlePasswordIconClick = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const handleRefreshReCaptcha = () => {
    setRefreshReCaptcha((r) => !r);
  };

  const isSubmitting = fetcher.state !== "idle";

  return (
    <fetcher.Form
      onSubmit={handleRefreshReCaptcha}
      method="post"
      action="/resources/create-account-wizard"
      className="flex flex-col gap-5"
      data-testid="create-account-form"
    >
      <input
        type="hidden"
        name="intent"
        value="create_account"
      />
      <GoogleReCaptcha
        onVerify={setToken}
        refreshReCaptcha={refreshReCaptcha}
      />
      <input
        type="hidden"
        name="g-recaptcha-response"
        value={token}
      />

      <ErrorMessage error={errors["form"]} />

      <div className="flex flex-col gap-0.5">
        <label
          className="text-sm leading-none text-blue900"
          htmlFor="email"
        >
          Email
        </label>
        <input
          aria-label="Enter email to create an account"
          ref={emailRef}
          id="email"
          className={`h-8 rounded border ${errors["email"] ? "border-red200" : "border-black"} p-2`}
          type="text"
          name="email"
        />
        <FieldError fieldError={errors["email"]} />
      </div>
      <div className="flex flex-col gap-1">
        <label
          className="text-sm leading-[0.5] text-blue900"
          htmlFor="password"
        >
          Password
        </label>
        <div className="relative flex">
          <input
            aria-label="Enter password"
            ref={passwordRef}
            id="password"
            className={`h-8 w-full rounded border ${errors["password"] ? "border-red200" : "border-black"} p-2`}
            type={isPasswordVisible ? "text" : "password"}
            name="password"
            value={password}
            onChange={handleInputChange}
          />
          <button
            type="button"
            aria-label="Toggle password visibility"
            className="absolute right-6 top-1.5 block"
            onClick={handlePasswordIconClick}
          >
            <svg
              className="fill-gray"
              width="20"
              height="20"
            >
              <use
                xlinkHref={`/frontend-assets/images/icons.svg#eye-${isPasswordVisible ? "open" : "closed"}`}
              />
            </svg>
          </button>
        </div>
        <FieldError fieldError={errors["password"]} />
      </div>
      <div className="flex flex-col gap-1">
        <label
          className="text-sm leading-[0.5] text-blue900"
          htmlFor="password_confirmation"
        >
          Password Confirmation
        </label>
        <div className="relative flex">
          <input
            aria-label="Confirm password"
            ref={passwordConfirmationRef}
            id="password_confirmation"
            className={`h-8 w-full rounded border ${errors["password_confirmation"] ? "border-red200" : "border-black"} p-2`}
            type={isPasswordVisible ? "text" : "password"}
            name="password_confirmation"
            value={passwordConfirmation}
            onChange={handleInputChange}
          />
          <button
            type="button"
            aria-label="Toggle password visibility"
            className="absolute right-6 top-1.5 block"
            onClick={handlePasswordIconClick}
          >
            <svg
              className="fill-gray"
              width="20"
              height="20"
            >
              <use
                xlinkHref={`/frontend-assets/images/icons.svg#eye-${isPasswordVisible ? "open" : "closed"}`}
              />
            </svg>
          </button>
        </div>
        <FieldError fieldError={errors["password_confirmation"]} />
      </div>

      <fieldset className="flex flex-col gap-1">
        <legend className="block text-sm text-blue900 mb-1">
          What Best Describes Your Use For Apparel?
        </legend>
        <div className="grid grid-cols-2 gap-1">
          <div className="flex items-center gap-1">
            <input
              id="business"
              type="radio"
              name="customer_type"
              value="business"
            />
            <label
              className="text-sm text-blue900"
              htmlFor="business"
            >
              Business
            </label>
          </div>
          <div className="flex items-center gap-1">
            <input
              id="school"
              type="radio"
              name="customer_type"
              value="school"
            />
            <label
              className="text-sm text-blue900"
              htmlFor="school"
            >
              School
            </label>
          </div>
          <div className="flex items-center gap-1">
            <input
              id="member"
              type="radio"
              name="customer_type"
              value="member"
            />
            <label
              className="text-sm text-blue900"
              htmlFor="member"
            >
              Personal
            </label>
          </div>
          <div className="flex items-center gap-1">
            <input
              id="charity"
              type="radio"
              name="customer_type"
              value="charity"
            />
            <label
              className="text-sm text-blue900"
              htmlFor="charity"
            >
              Charity
            </label>
          </div>
        </div>
        <FieldError fieldError={errors["customer_type"]} />
      </fieldset>

      <div className="flex items-center justify-start gap-2">
        <SubmitButton
          disabled={isSubmitting}
          label="Create account"
        />
        <p className="text-gray text-sm">
          Or <SignInCta />
        </p>
      </div>
    </fetcher.Form>
  );
};

export default CreateAccount;
